/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { differenceInCalendarDays } from "date-fns/differenceInCalendarDays";
import { differenceInHours } from "date-fns/differenceInHours";
import { Fragment } from "react";

import { spacing } from "../../../../../../global/emotion";
import { useCountdown } from "../../../../../hooks/useCountdown";
import { cssMediaQueryMinWidthTablet } from "../../../../../lib/constants/emotionMediaQueries.constants";
import type { BlockProps } from "../../../../refactor/Block/Block.emotion";
import { Block } from "../../../../refactor/Block/Block.emotion";
import type { ChallengeMediumProps } from "../ChallengeMedium/ChallengeMedium.emotion";
import { ChallengeMedium } from "../ChallengeMedium/ChallengeMedium.emotion";

const actionsStyle: Interpolation = {
  marginTop: spacing.m,
};
const blockStyle: Interpolation = {
  display: "none",
  [cssMediaQueryMinWidthTablet]: { display: "flex" },
};
const descriptionStyle: Interpolation = {
  paddingTop: spacing.s,
};
const challengeMediumStyle: Interpolation = {
  display: "block",
  [cssMediaQueryMinWidthTablet]: { display: "none" },
};

export type ChallengeLargeProps = Pick<
  ChallengeMediumProps,
  | "asChild"
  | "activeState"
  | "availableAt"
  | "children"
  | "color"
  | "description"
  | "endsAt"
  | "focusState"
  | "hoverState"
  | "imgBlur"
  | "imgSrc"
  | "rightActions"
  | "subscription"
  | "tags"
  | "title"
  | "centerActions"
  | "imgContainerProps"
> &
  Pick<BlockProps, "actions" | "descriptionProps">;

export const ChallengeLarge = ({
  asChild,
  actions,
  activeState = false,
  availableAt,
  children,
  color = "black",
  description,
  descriptionProps,
  endsAt,
  focusState = true,
  hoverState = true,
  imgBlur,
  imgSrc,
  rightActions,
  subscription,
  tags = [],
  title,
  centerActions,
  imgContainerProps,
}: ChallengeLargeProps) => {
  const t = useTranslations();

  const countdown = useCountdown({
    targetDate: availableAt,
    format: "default",
  });
  let dateTag: null | string = null;

  if (availableAt && endsAt) {
    const date = new Date();
    const ended = date > new Date(endsAt);
    const started = date > new Date(availableAt);
    const dayDiff = differenceInCalendarDays(new Date(endsAt), date);
    const hourDiff = differenceInHours(new Date(endsAt), date);

    dateTag = ended
      ? null
      : started
        ? dayDiff <= 0
          ? t("challenge_tile_hours_left_label", {
              challenge_tile_hours_left_label: hourDiff,
            })
          : t("challenge_tile_days_left_label", {
              challenge_tile_days_left_label: dayDiff,
            })
        : t("challenge_tile_starts_in", {
            0: countdown,
          });
  }

  return (
    <Fragment>
      <Block
        actions={actions}
        actionsCSS={actionsStyle}
        activeState={activeState}
        color={color}
        description={description}
        descriptionCSS={descriptionStyle}
        descriptionProps={descriptionProps}
        focusState={focusState}
        hoverState={hoverState}
        imgBlur={imgBlur}
        imgSrc={imgSrc}
        css={blockStyle}
        subscription={subscription}
        tags={dateTag ? [...tags, dateTag] : tags}
        title={title}
        imgContainerProps={imgContainerProps}
      />
      <ChallengeMedium
        activeState={activeState}
        asChild={asChild}
        children={children}
        color={color}
        description={description}
        focusState={focusState}
        hoverState={hoverState}
        imgBlur={imgBlur}
        imgSrc={imgSrc}
        rightActions={rightActions}
        css={challengeMediumStyle}
        subscription={subscription}
        tags={dateTag ? [...tags, dateTag] : tags}
        title={title}
        centerActions={centerActions}
        imgContainerProps={imgContainerProps}
      />
    </Fragment>
  );
};
